import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Search, Loader2, ClipboardList, Contact, Wrench, Package } from "lucide-react";
import { currency, formatDate } from "../lib/utils-app";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const h = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Keyboard shortcut: Ctrl/Cmd+K
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const el = document.querySelector('[data-testid="global-search-input"]');
        if (el) el.focus();
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const term = q.trim();
    if (term.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(term)}`);
        setResults(data);
      } catch {
        setResults({ orders: [], customers: [], vendors: [], items: [] });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(timerRef.current);
  }, [q]);

  const totalCount = results
    ? results.orders.length +
      results.customers.length +
      results.vendors.length +
      results.items.length
    : 0;

  const goOrder = (o) => {
    navigate(`/orders?highlight=${o.id}`);
    setOpen(false);
    setQ("");
  };
  const goCustomer = () => {
    navigate(`/customers`);
    setOpen(false);
    setQ("");
  };
  const goVendor = () => {
    navigate(`/vendors`);
    setOpen(false);
    setQ("");
  };

  return (
    <div className="relative" ref={boxRef} data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => q.trim().length >= 2 && setOpen(true)}
          placeholder="Search orders, customers, vendors, items…"
          data-testid="global-search-input"
          className="w-full h-10 pl-9 pr-12 rounded-md border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm text-slate-900 placeholder:text-slate-400 transition-colors"
        />
        <kbd className="hidden md:inline-flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-0.5 px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[10px] font-semibold text-slate-500 tracking-wider">
          Ctrl K
        </kbd>
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-[70vh] overflow-y-auto z-50"
          data-testid="global-search-dropdown"
        >
          {loading && (
            <div className="p-4 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          )}
          {!loading && results && totalCount === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">
              No matches for <span className="font-semibold">"{q}"</span>
            </div>
          )}
          {!loading && results && totalCount > 0 && (
            <div className="divide-y divide-slate-100">
              {results.orders.length > 0 && (
                <ResultGroup title="Orders" icon={ClipboardList} count={results.orders.length}>
                  {results.orders.map((o) => (
                    <button
                      key={o.id}
                      onClick={() => goOrder(o)}
                      data-testid="search-result-order"
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left"
                    >
                      <div className="h-8 w-8 rounded-md bg-red-50 text-red-700 font-mono text-[11px] font-bold flex items-center justify-center shrink-0">
                        {(o.order_number || "").slice(-3)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {o.customer_name || "—"}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          #{o.order_number} · {o.customer_phone || "no phone"} · {formatDate(o.delivery_date)}
                        </div>
                      </div>
                      <div className="text-xs font-semibold text-slate-700 tabular-nums shrink-0">
                        {currency(o.total_amount || 0)}
                      </div>
                    </button>
                  ))}
                </ResultGroup>
              )}
              {results.customers.length > 0 && (
                <ResultGroup title="Customers" icon={Contact} count={results.customers.length}>
                  {results.customers.map((c) => (
                    <button
                      key={c.id}
                      onClick={goCustomer}
                      data-testid="search-result-customer"
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left"
                    >
                      <div className="h-8 w-8 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center shrink-0">
                        {(c.name || c.phone || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {c.name || "—"}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {c.phone} · {c.order_count || 0} orders
                        </div>
                      </div>
                    </button>
                  ))}
                </ResultGroup>
              )}
              {results.vendors.length > 0 && (
                <ResultGroup title="Vendors" icon={Wrench} count={results.vendors.length}>
                  {results.vendors.map((v) => (
                    <button
                      key={v.id}
                      onClick={goVendor}
                      data-testid="search-result-vendor"
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left"
                    >
                      <div className="h-8 w-8 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                        <Wrench className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {v.name}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate capitalize">
                          {v.category || "vendor"} · {v.phone || "no phone"}
                        </div>
                      </div>
                    </button>
                  ))}
                </ResultGroup>
              )}
              {results.items.length > 0 && (
                <ResultGroup title="Items" icon={Package} count={results.items.length}>
                  {results.items.map((it, i) => (
                    <div
                      key={it.name + i}
                      data-testid="search-result-item"
                      className="flex items-center gap-3 px-4 py-2.5"
                    >
                      <div className="h-8 w-8 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                        <Package className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {it.name}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {it.orders} order{it.orders === 1 ? "" : "s"} · qty {it.total_qty}
                        </div>
                      </div>
                    </div>
                  ))}
                </ResultGroup>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({ title, icon: Icon, count, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">
        <Icon className="h-3 w-3" />
        {title}
        <span className="ml-auto text-slate-400 normal-case">{count}</span>
      </div>
      {children}
    </div>
  );
}
